// Feedback Email Notification Service
// Handles sending email notifications when feedback is submitted

import { apiClient } from '@/api/base44Client';

export const FeedbackEmailService = {
  // Send notification email when feedback is submitted
  async sendFeedbackNotification(feedbackData) {
    try {
      const emailData = {
        to: this.getAdminEmails(),
        subject: `New ${feedbackData.feedback_type.replace('_', ' ')} Feedback - ${feedbackData.priority.toUpperCase()} Priority`,
        template: 'feedback-notification',
        data: {
          feedback: feedbackData,
          adminUrl: `${window.location.origin}/admin/feedback`,
          timestamp: new Date().toLocaleString()
        }
      };

      await apiClient.sendEmail(emailData);
      console.log('Feedback notification email sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending feedback notification email:', error);
      return false;
    }
  },

  // Send confirmation email to user who submitted feedback
  async sendUserConfirmation(feedbackData) {
    try {
      if (!feedbackData.user_email) {
        console.log('No user email provided, skipping confirmation email');
        return true;
      }

      const emailData = {
        to: feedbackData.user_email,
        subject: 'Thank you for your feedback - Summer Squad',
        template: 'feedback-confirmation',
        data: {
          feedback: feedbackData,
          userEmail: feedbackData.user_email,
          timestamp: new Date().toLocaleString()
        }
      };

      await apiClient.sendEmail(emailData);
      console.log('Feedback confirmation email sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending feedback confirmation email:', error);
      return false;
    }
  },

  // Send notification when feedback status changes
  async sendStatusUpdateNotification(feedbackData, oldStatus, newStatus) {
    try {
      if (!feedbackData.user_email) {
        console.log('No user email provided, skipping status update email');
        return true;
      }

      const emailData = {
        to: feedbackData.user_email,
        subject: `Your feedback status has been updated - Summer Squad`,
        template: 'feedback-status-update',
        data: {
          feedback: feedbackData,
          oldStatus,
          newStatus,
          timestamp: new Date().toLocaleString()
        }
      };

      await apiClient.sendEmail(emailData);
      console.log('Feedback status update email sent successfully');
      return true;
    } catch (error) {
      console.error('Error sending feedback status update email:', error);
      return false;
    }
  },

  // Get admin email addresses from environment or config
  getAdminEmails() {
    // In production, this would come from environment variables or database config
    const adminEmails = [
      'admin@campconnect.app',
      'feedback@campconnect.app'
    ];

    // Add any additional admin emails from environment (only if available)
    try {
      const envAdminEmails = import.meta.env.VITE_FEEDBACK_ADMIN_EMAILS;
      if (envAdminEmails) {
        adminEmails.push(...envAdminEmails.split(','));
      }
    } catch (error) {
      // Environment variable not available, use defaults
      console.log('Using default admin emails');
    }

    return adminEmails;
  },

  // Generate HTML email template for feedback notification
  generateNotificationHTML(feedbackData) {
    const priorityColors = {
      urgent: '#dc2626',
      high: '#ea580c',
      medium: '#d97706',
      low: '#16a34a'
    };

    const typeIcons = {
      bug: '🐛',
      feature_request: '💡',
      general: '💬'
    };

    const statusColors = {
      new: '#2563eb',
      in_progress: '#d97706',
      resolved: '#16a34a',
      closed: '#6b7280'
    };

    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>New Feedback - Summer Squad</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #685EFF, #0d9488); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center; }
          .content { background: white; padding: 30px; border: 1px solid #e5e7eb; }
          .footer { background: #f9fafb; padding: 20px; border-radius: 0 0 10px 10px; text-align: center; font-size: 14px; color: #6b7280; }
          .badge { display: inline-block; padding: 4px 12px; border-radius: 20px; font-size: 12px; font-weight: 600; text-transform: uppercase; }
          .priority-urgent { background: ${priorityColors.urgent}; color: white; }
          .priority-high { background: ${priorityColors.high}; color: white; }
          .priority-medium { background: ${priorityColors.medium}; color: white; }
          .priority-low { background: ${priorityColors.low}; color: white; }
          .status-new { background: ${statusColors.new}; color: white; }
          .status-in_progress { background: ${statusColors.in_progress}; color: white; }
          .status-resolved { background: ${statusColors.resolved}; color: white; }
          .status-closed { background: ${statusColors.closed}; color: white; }
          .info-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; margin: 20px 0; }
          .info-item { padding: 15px; background: #f9fafb; border-radius: 8px; }
          .info-label { font-size: 12px; color: #6b7280; text-transform: uppercase; font-weight: 600; margin-bottom: 5px; }
          .info-value { font-weight: 600; }
          .description { background: #f3f4f6; padding: 20px; border-radius: 8px; margin: 20px 0; white-space: pre-wrap; }
          .cta-button { display: inline-block; background: #685EFF; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 20px 0; }
          .cta-button:hover { background: #5448cc; }
          .meta-info { font-size: 12px; color: #6b7280; margin-top: 20px; padding-top: 20px; border-top: 1px solid #e5e7eb; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>${typeIcons[feedbackData.feedback_type]} New Feedback Received</h1>
            <p>Summer Squad Feedback System</p>
          </div>
          
          <div class="content">
            <h2>${feedbackData.title || `${feedbackData.feedback_type.replace('_', ' ')} Feedback`}</h2>
            
            <div class="info-grid">
              <div class="info-item">
                <div class="info-label">Type</div>
                <div class="info-value">${feedbackData.feedback_type.replace('_', ' ')}</div>
              </div>
              <div class="info-item">
                <div class="info-label">Priority</div>
                <div class="info-value">
                  <span class="badge priority-${feedbackData.priority}">${feedbackData.priority}</span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Status</div>
                <div class="info-value">
                  <span class="badge status-${feedbackData.status}">${feedbackData.status}</span>
                </div>
              </div>
              <div class="info-item">
                <div class="info-label">Submitted</div>
                <div class="info-value">${new Date(feedbackData.created_at).toLocaleString()}</div>
              </div>
            </div>
            
            <div class="info-item">
              <div class="info-label">User</div>
              <div class="info-value">${feedbackData.user_email || 'Anonymous'}</div>
            </div>
            
            <div class="info-item">
              <div class="info-label">Page URL</div>
              <div class="info-value">${feedbackData.page_url}</div>
            </div>
            
            <h3>Description</h3>
            <div class="description">${feedbackData.description}</div>
            
            <div style="text-align: center;">
              <a href="${window.location.origin}/admin/feedback" class="cta-button">
                View in Admin Panel
              </a>
            </div>
            
            <div class="meta-info">
              <p><strong>Feedback ID:</strong> ${feedbackData.id}</p>
              <p><strong>Browser:</strong> ${feedbackData.user_agent}</p>
              <p><strong>Timestamp:</strong> ${new Date().toLocaleString()}</p>
            </div>
          </div>
          
          <div class="footer">
            <p>This is an automated notification from the Summer Squad feedback system.</p>
            <p>Please do not reply to this email.</p>
          </div>
        </div>
      </body>
      </html>
    `;
  },

  // Generate HTML email template for user confirmation
  generateConfirmationHTML(feedbackData) {
    return `
      <!DOCTYPE html>
      <html>
      <head>
        <meta charset="utf-8">
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Thank you for your feedback - Summer Squad</title>
        <style>
          body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; line-height: 1.6; color: #333; }
          .container { max-width: 600px; margin: 0 auto; padding: 20px; }
          .header { background: linear-gradient(135deg, #685EFF, #0d9488); color: white; padding: 30px; border-radius: 10px 10px 0 0; text-align: center; }
          .content { background: white; padding: 30px; border: 1px solid #e5e7eb; }
          .footer { background: #f9fafb; padding: 20px; border-radius: 0 0 10px 10px; text-align: center; font-size: 14px; color: #6b7280; }
          .thank-you { text-align: center; margin: 30px 0; }
          .feedback-summary { background: #f9fafb; padding: 20px; border-radius: 8px; margin: 20px 0; }
          .cta-button { display: inline-block; background: #685EFF; color: white; padding: 12px 24px; text-decoration: none; border-radius: 6px; font-weight: 600; margin: 20px 0; }
          .cta-button:hover { background: #5448cc; }
        </style>
      </head>
      <body>
        <div class="container">
          <div class="header">
            <h1>Thank You for Your Feedback!</h1>
            <p>Summer Squad</p>
          </div>
          
          <div class="content">
            <div class="thank-you">
              <h2>We've received your feedback</h2>
              <p>Thank you for taking the time to share your thoughts with us. Your feedback is valuable and helps us improve Summer Squad for everyone.</p>
            </div>
            
            <div class="feedback-summary">
              <h3>Your Feedback Summary</h3>
              <p><strong>Type:</strong> ${feedbackData.feedback_type.replace('_', ' ')}</p>
              <p><strong>Submitted:</strong> ${new Date(feedbackData.created_at).toLocaleString()}</p>
              <p><strong>Description:</strong> ${feedbackData.description}</p>
            </div>
            
            <div style="text-align: center;">
              <a href="${window.location.origin}" class="cta-button">
                Continue Using Summer Squad
              </a>
            </div>
            
            <p>We'll review your feedback and get back to you if we need any additional information. In the meantime, feel free to continue exploring Summer Squad!</p>
          </div>
          
          <div class="footer">
            <p>This is an automated confirmation from Summer Squad.</p>
            <p>If you have any questions, please contact us at support@campconnect.app</p>
          </div>
        </div>
      </body>
      </html>
    `;
  }
};
